<template>
  <section class="property-gallery mt-4" :key="property?.id || 1">
    <Skeleton v-if="!isImageLoaded && hasCoverImage"/>
    <picture v-show="hasCoverImage">
      <source media="(max-width: 430px)" :srcset="coverImage.mobile || undefined" />
      <img
          v-if="coverImage.full"
          :src="coverImage.full"
          :alt="property.name"
          width="1120"
          height="430"
          @load="onImageLoad"
          @error="isImageLoaded = false"
      >
    </picture>
    <PhotoPlaceholder v-show="!hasCoverImage"/>
    <div
        v-if="imagesQty > 1"
        @click="isModalVisible = true"
        class="gallery-preview__total text-caption">
      <span class="icon icon-image"></span>
      +{{imagesQty - 1}} {{$t('photos')}}
    </div>

    <DialogCloseOutside
        v-model="isModalVisible"
        :showHeader="false"
        position="right"
        :block-scroll="true"
        :modal="true"
        :draggable="false"
        class="gallery-modal-container room-modal-container custom-scrollbar"
    >
      <div class="pb-4 gallery-modal-header">
          <button class="mobile-only" aria-label="Close gallery" @click="isModalVisible = false">
            <span class="icon icon-chevron-left color-blue text-title-1"></span>
          </button>
          <span class="flex-1 text-title-1 fw-bold room-info-dialog-header">{{$t('Photos')}}</span>
          <button class="desktop-only" aria-label="Open gallery" @click="isModalVisible = false">
            <span class="icon icon-close color-blue text-title-1"></span>
          </button>
        </div>
      <div class="gallery-modal-filters">

          <!-- Buttons for selecting categories -->
          <button
              class="category-button btn btn-tag"
              :class="{ 'selected': selectedCategory === 'All' }"
              @click="selectedCategory = 'All'"
          >
            {{$t('All')}} ({{countImgQty(property.images)}})
          </button>
          <button
              v-for="category in imagesCategories"
              :key="category"
              class="category-button btn btn-tag"
              :class="{ 'selected': selectedCategory === category }"
              @click="selectedCategory = category"
          >
            {{ category }} ({{property.images[category].length}})
          </button>

        </div>
      <div class="gallery-photos-wrapper">
          <PropertyPageGallerySection
              v-for="(photos, arrayIndex) in filteredImagesArrays"
              :key="arrayIndex"
              :photos="photos"
              :header="imagesCategories[selectedCategoryIndex > -1 ?  selectedCategoryIndex : arrayIndex]"
              @image-click="(idx: number) => openLightbox(arrayIndex as number, idx)"
          />
        </div>
    </DialogCloseOutside>

    <ImageLightbox
      v-model:visible="lightboxVisible"
      :images="lightboxImages"
      :start-index="lightboxStartIndex"
    />
  </section>
</template>

<script setup lang="ts">
import {usePropertyStore} from '~/store/property'
import PropertyPageGallerySection from "~/components/PropertyPage/PropertyPageGallerySection.vue";
import { imageUrlWithProxy } from '~/utils/imageproxy'
import PhotoPlaceholder from "~/components/PhotoPlaceholder.vue";
import DialogCloseOutside from "~/components/DialogCloseOutside.vue";
const propertyStore = usePropertyStore()
const property = computed(() => propertyStore.property)
let isImageLoaded = ref(false as boolean)
let isModalVisible = ref(false as boolean)
let selectedCategory = ref('All')

const lightboxVisible = ref(false)
const lightboxStartIndex = ref(0)

//computed
const images = computed(() => property.value.images || {})
const imagesQty = computed(() => countImgQty(property.value.images || {}))
const imagesCategories = computed(() => Object.keys(property.value.images || {}))
const imagesArrays = computed(() => Object.values(property.value.images || {}))
const filteredImagesArrays = computed(() => {
  if (selectedCategoryIndex.value > -1) {
    // Return only the selected category's images
    return [imagesArrays.value[selectedCategoryIndex.value]] as any[];
  } else {
    // Return all images if no specific category is selected
    return imagesArrays.value;
  }
});

const hasCoverImage = ref(!!property.value.cover_image?.url?.length)

const selectedCategoryIndex = computed(() => imagesCategories.value.indexOf(selectedCategory.value))

const coverImage = computed(() => {
  return {
    full: property.value.cover_image?.url ? imageUrlWithProxy(property.value.cover_image?.url, 1120, 430) : null,
    mobile: property.value.cover_image?.url ? imageUrlWithProxy(property.value.cover_image?.url, 430, 298) : null,
  }
})

const lightboxImages = computed(() => {
  return (filteredImagesArrays.value as any[]).flat()
})

function openLightbox(sectionIndex: number, imageIndex: number) {
  // Calculate the global index across all visible sections
  let globalIndex = imageIndex
  for (let i = 0; i < sectionIndex; i++) {
    globalIndex += (filteredImagesArrays.value[i] as any[]).length
  }
  lightboxStartIndex.value = globalIndex
  lightboxVisible.value = true
}

function countImgQty(images: any) {
  let _qty = 0;
  for (const category in images) {
    _qty += images[category].length;
  }
  return _qty;
}
function onImageLoad() {
  isImageLoaded.value = true
  hasCoverImage.value = true
}

</script>

<style lang="scss" scoped>
.property-gallery {
  height: 430px;
  position: relative;

  //vertical tablet
  @media screen and (max-width: 768px) and (min-width: 431px){
    height: 280px;
  }

  @media screen and (max-width: 430px) {
    max-height: 298px;
  }

  img {
    width: 100%;
    max-width: 100%;
    height: 430px;
    object-fit: cover;
    object-position: center;
    border-radius: 8px;
    position: relative;
    z-index: 1;

    @media screen and (max-width: 768px) and (min-width: 431px){
      height: 280px;
    }

    @media screen and (max-width: 430px) {
      max-width: inherit;
      left: -16px;
      width: 100vw;
      max-height: 298px;
      border-radius: 0;
    }
  }
}

.gallery-modal-header {
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    gap: 24px;
  }

  button {
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
  }
}

.gallery-modal-filters {
  gap: 8px;
  display: flex;
  flex-wrap: wrap;
  padding-bottom: 4px;

  .category-button {
    height: 32px;
  }
}

.gallery-photos-wrapper {
  @media (max-width: 768px) {
    max-height: 100vh;
    padding-right: 10px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: var(--white);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--grey);
    border-radius: 10px;
  }
}

.gallery-preview__total {
  position: absolute;
  right: 8px;
  top: 12px;
  padding: 6px 11px;
  border-radius: 15px;
  background-color: var(--dark-grey);
  color: var(--white);
  z-index: 1;
  cursor: pointer;

  display: flex;
  align-items: center;
  gap: 4px;

  .icon {
    font-size: 12
    px;
  }

  //tablet vertical
  @media screen and (max-width: 768px) and (min-width: 431px){
    right: 12px;
  }

  @media (max-width: 430px) {
    right: -12px;
  }
}

:global(.property-gallery .p-skeleton) {
  position: absolute !important;
  width: 100% !important;
  max-width: 100% !important;
  height: 430px !important;
  border-radius: 8px !important;

  //vertical tablet
  @media screen and (max-width: 768px) and (min-width: 431px){
    height: 280px !important;
  }

  @media screen and (max-width: 430px) {
    height: 298px !important;
  }
}
</style>
